import React, { useState, useEffect, useCallback, Fragment } from 'react';
import { Tabs, Tab } from 'react-bootstrap';
import { connect } from 'react-redux';
import BucketList from './BucketList';
import UnassignedStaff from './UnassignedStaff';
import BucketStaff from './BucketStaff';
import CreateBucketModal from './modals/CreateBucketModal';
import EditBucketModal from './modals/EditBucketModal';
import AssignActivityPlanModal from './modals/AssignActivityPlanModal';
import { fetchWrapper, getTeamIdFromURL } from '@/_helpers';
import { toast } from 'react-toastify';

const BucketManagement = ({ teams, currUser }) => {
    const [buckets, setBuckets] = useState([]);
    const [unassignedStaff, setUnassignedStaff] = useState([]);
    const [bucketStatistics, setBucketStatistics] = useState(null);
    const [providerId, setProviderId] = useState(null);

    const [isLoading, setIsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('buckets');

    const [selectedBucket, setSelectedBucket] = useState(null);

    const [bucketForm, setBucketForm] = useState({
        name: '',
        description: '',
        color: '#3498db',
        icon: 'bucket',
    });

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showAssignActivityPlanModal, setShowAssignActivityPlanModal] = useState(false);

    const [selectedActivityPlan, setSelectedActivityPlan] = useState(null);

    const teamId = getTeamIdFromURL();

    const fetchProvider = async () => {
        try {
            setIsLoading(true);

            const {
                results: [response],
            } = await fetchWrapper.get(`evv/provider?team=${teamId}`);
            setProviderId(response._id);
        } catch (e) {}
    };

    const fetchBuckets = async () => {
        try {
            setIsLoading(true);
            const response = await fetchWrapper.get(
                `staffBuckets?team=${teamId}&provider=${providerId}&sortBy=sequenceNumber:asc`
            );
            setBuckets(response.results || []);
            return response.results || [];
        } catch (error) {
            toast.error('Failed to fetch buckets');
            console.error('Error fetching buckets:', error);
            return [];
        } finally {
            setIsLoading(false);
        }
    };

    const fetchBucketStatistics = async () => {
        try {
            const response = await fetchWrapper.get(`staffBuckets/statistics?provider=${providerId}&team=${teamId}`);
            setBucketStatistics(response);
        } catch (error) {
            console.error('Error fetching bucket statistics:', error);
        }
    };

    const fetchUnassignedStaff = async () => {
        try {
            const response = await fetchWrapper.get(
                `evv/employee?team=${teamId}&bucketStatus=unassigned&archieved=false&provider=${providerId}&limit=100`
            );
            setUnassignedStaff(response.results || []);
        } catch (error) {
            console.error('Error fetching unassigned staff:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const loadData = async () => {
        try {
            await Promise.all([fetchBuckets(), fetchBucketStatistics()]);
        } catch (err) {
            console.error('Error loading data:', err);
        }
    };

    useEffect(() => {
        fetchProvider();
    }, []);

    useEffect(() => {
        if (providerId) {
            if (activeTab === 'unassigned-staffs') {
                fetchUnassignedStaff();
            } else if (activeTab === 'buckets') {
                loadData();
            }
        }
    }, [providerId, activeTab]);

    const handleCreateBucket = async () => {
        try {
            setIsLoading(true);
            await fetchWrapper.post(`staffBuckets?team=${teamId}`, {
                ...bucketForm,
                provider: providerId,
            });

            toast.success('Bucket created successfully');
            setShowCreateModal(false);
            setBucketForm({
                name: '',
                description: '',
                color: '#3498db',
                icon: 'bucket',
            });

            await loadData();
        } catch (error) {
            toast.error('Failed to create bucket');
            console.error('Error creating bucket:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateDefaultBuckets = async () => {
        try {
            setIsLoading(true);
            await fetchWrapper.post(`staffBuckets/defaults?team=${teamId}&provider=${providerId}`);
            toast.success('Default buckets created successfully');
            await fetchBuckets();
        } catch (error) {
            toast.error(error?.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDragEnd = async (result) => {
        if (!result.destination) return;
        const items = Array.from(buckets);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);
        const previousBuckets = Array.from(buckets);
        setBuckets(items);

        try {
            await fetchWrapper.put(`staffBuckets/sequence?provider=${providerId}&team=${teamId}`, {
                bucketSequence: items.map((bucket) => bucket._id),
            });
        } catch (error) {
            toast.error('Failed to update bucket sequence');
            console.error('Error updating bucket sequence:', error);
            setBuckets(previousBuckets);
        }
    };

    const handleUpdateBucket = async () => {
        try {
            setIsLoading(true);
            await fetchWrapper.put(
                `staffBuckets/${selectedBucket._id}?provider=${providerId}&team=${teamId}`,
                bucketForm
            );

            toast.success('Bucket updated successfully');
            setShowEditModal(false);
            await fetchBuckets();
        } catch (error) {
            toast.error('Failed to update bucket');
            console.error('Error updating bucket:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleAssignActivityPlan = async () => {
        try {
            setIsLoading(true);
            await fetchWrapper.post(`staffBuckets/${selectedBucket._id}/activity-plan?team=${teamId}`, {
                activityPlanId: selectedActivityPlan.value,
            });

            toast.success('Activity plan assigned successfully');
            setShowAssignActivityPlanModal(false);
            setSelectedActivityPlan(null);

            await fetchBuckets();
        } catch (error) {
            toast.error('Failed to assign activity plan');
            console.error('Error assigning activity plan:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const renderContent = () => {
        switch (activeTab) {
            case 'buckets':
                return (
                    <BucketList
                        buckets={buckets}
                        bucketStatistics={bucketStatistics}
                        isBusy={isLoading}
                        onShowCreateModal={() => setShowCreateModal(true)}
                        onEditBucket={(bucket) => {
                            setSelectedBucket(bucket);
                            setBucketForm({
                                name: bucket.name,
                                description: bucket.description || '',
                                color: bucket.color || '#3498db',
                                icon: bucket.icon || 'bucket',
                            });
                            setShowEditModal(true);
                        }}
                        onAssignActivityPlan={(bucket) => {
                            setSelectedBucket(bucket);
                            setSelectedActivityPlan(
                                bucket.activityPlan
                                    ? { value: bucket.activityPlan._id, label: bucket.activityPlan.name }
                                    : null
                            );
                            setShowAssignActivityPlanModal(true);
                        }}
                        onViewStaff={(bucket) => {
                            setSelectedBucket(bucket);
                            setActiveTab(`bucket-${bucket._id}`);
                        }}
                        onDragEnd={handleDragEnd}
                        handleCreateDefaultBuckets={handleCreateDefaultBuckets}
                        loadData={loadData}
                    />
                );
            case 'unassigned-staffs':
                return (
                    <UnassignedStaff
                        staff={unassignedStaff}
                        isLoading={isLoading}
                        providerId={providerId}
                        onSave={fetchUnassignedStaff}
                    />
                );
            default:
                if (activeTab.startsWith('bucket-')) {
                    const bucketId = activeTab.replace('bucket-', '');
                    const bucket = buckets.find((b) => b._id === bucketId);
                    if (bucket) {
                        return (
                            <BucketStaff
                                bucket={bucket}
                                staff={bucket.staffMembers || []}
                                isLoading={isLoading}
                                onBack={() => setActiveTab('buckets')}
                                providerId={providerId}
                                onSave={fetchBuckets}
                            />
                        );
                    }
                }
                return <BucketList />;
        }
    };

    return (
        <Fragment>
            <div className="d-flex align-items-center justify-content-between">
                <div>
                    <h2 className="mt-2 mb-4 ">Staff Bucket Management</h2>
                </div>
                <div>
                    <Tabs activeKey={activeTab} onSelect={setActiveTab} className="mb-0">
                        <Tab eventKey="buckets" title="Buckets" />
                        <Tab eventKey="unassigned-staffs" title="Unassigned Staffs" />
                    </Tabs>
                </div>
            </div>

            {renderContent()}

            <CreateBucketModal
                show={showCreateModal}
                onHide={() => setShowCreateModal(false)}
                bucketForm={bucketForm}
                onChange={setBucketForm}
                onCreate={handleCreateBucket}
                isLoading={isLoading}
            />

            <EditBucketModal
                show={showEditModal}
                onHide={() => setShowEditModal(false)}
                bucketForm={bucketForm}
                onChange={setBucketForm}
                onUpdate={handleUpdateBucket}
                isLoading={isLoading}
            />

            <AssignActivityPlanModal
                show={showAssignActivityPlanModal}
                onHide={() => setShowAssignActivityPlanModal(false)}
                selectedActivityPlan={selectedActivityPlan}
                onChange={setSelectedActivityPlan}
                // activityPlans={activityPlans}
                bucket={selectedBucket}
                onAssign={handleAssignActivityPlan}
                isLoading={isLoading}
                teamId={teamId}
            />
        </Fragment>
    );
};

const mapStateToProps = (state) => ({
    teams: state.teams.teams,
    currUser: state.user.user,
});

export default connect(mapStateToProps)(BucketManagement);
